import { useMutation, useQueryClient, UseMutationOptions } from '@tanstack/react-query';
import { useSubscription } from './useSubscription';
import type { ApiClient } from '../api/index';

export interface CrudListOptions {
  subscriptionFn?: () => WebSocket | null;
  query?: Record<string, string>;
  enabled?: boolean;
  [key: string]: unknown;
}

export interface CrudGetOptions {
  subscriptionFn?: () => WebSocket | null;
  query?: Record<string, string>;
  enabled?: boolean;
  [key: string]: unknown;
}

export function useCrud<T = unknown>(apiClient: ApiClient) {
  const baseKey = apiClient.baseUrl;

  function useList(options: CrudListOptions = {}) {
    const { subscriptionFn, query, enabled = true, ...queryOpts } = options;
    return useSubscription<T[]>({
      queryKey: [baseKey, '/', query],
      queryFn: () => apiClient.get<T[]>('/', { query }),
      subscriptionFn,
      enabled,
      ...queryOpts,
    });
  }

  function useGet(id: string | number, options: CrudGetOptions = {}) {
    const { subscriptionFn, query, enabled, ...queryOpts } = options;
    return useSubscription<T>({
      queryKey: [baseKey, `/${id}`, query],
      queryFn: () => apiClient.get<T>(`/${id}`, { query }),
      subscriptionFn,
      enabled: !!id && (enabled !== false),
      ...queryOpts,
    });
  }

  function useCreate(mutationOptions: UseMutationOptions<T, Error, unknown> = {}) {
    const qc = useQueryClient();
    return useMutation<T, Error, unknown>({
      mutationFn: (body) => apiClient.post<T>('/', { body }),
      onSuccess: () => qc.invalidateQueries({ queryKey: [baseKey] }),
      ...mutationOptions,
    });
  }

  function useUpdate(mutationOptions: UseMutationOptions<T, Error, { id: string | number; body: unknown }> = {}) {
    const qc = useQueryClient();
    return useMutation<T, Error, { id: string | number; body: unknown }>({
      mutationFn: ({ id, body }) => apiClient.put<T>(`/${id}`, { body }),
      onSuccess: () => qc.invalidateQueries({ queryKey: [baseKey] }),
      ...mutationOptions,
    });
  }

  function useRemove(mutationOptions: UseMutationOptions<T, Error, string | number> = {}) {
    const qc = useQueryClient();
    return useMutation<T, Error, string | number>({
      mutationFn: (id) => apiClient.delete<T>(`/${id}`),
      onSuccess: () => qc.invalidateQueries({ queryKey: [baseKey] }),
      ...mutationOptions,
    });
  }

  return { useList, useGet, useCreate, useUpdate, useRemove };
}
